import { Card } from '@components/ui/card'
import { Skeleton } from '@components/ui/skeleton'
import { cn } from '@/lib/utils'
import { CookingPot } from 'lucide-react' // Import the icon

// Updated style block with enhanced animation
const loadingBarStyle = `
  @keyframes loadingBar {
    0% {
      transform: translateX(-100%);
    }
    50% {
      transform: translateX(200%);
    }
    100% {
      transform: translateX(-100%);
    }
  }
`

const ModelResultSkeleton = ({ className }) => {
  return (
    <>
      <style>{loadingBarStyle}</style>
      <Card className={cn('w-full flex flex-col md:flex-row overflow-hidden bg-white border shadow-sm', className)}>
        {/* Footer with thumbnails */}
        <div className="border-t md:border-t-0 md:border-l border-gray-200 p-4 bg-gray-50">
          <div className="flex flex-row md:flex-col gap-2 md:gap-3 overflow-x-auto md:overflow-x-visible">
            {[1, 2].map((i) => (
              <Skeleton
                key={i}
                className="w-20 h-20 md:w-40 md:h-40 rounded-md bg-gray-200 flex-shrink-0 animate-pulse"
              />
            ))}
          </div>
        </div>
        {/* Main content area */}
        <div className="p-4 md:p-6 w-full space-y-4 md:space-y-6">
          {/* Progress indicator */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-base md:text-lg font-semibold text-gray-800">Generating your media</h3>
              <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
            </div>

            <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden relative">
              <div className="absolute h-full w-1/4 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-full animate-[loadingBar_2s_ease-in-out_infinite]" />
            </div>

            <p className="text-gray-600 text-xs md:text-sm animate-pulse">
              Processing your creation... Please wait a moment.
            </p>
          </div>

          {/* animated placeholder */}
          <div className="relative w-full aspect-video rounded-xl overflow-hidden border border-gray-200 shadow-inner">
            <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 animate-[pulse_3s_ease-in-out_infinite]">
              {/*  processing dots */}
              <div className="absolute inset-0 opacity-40">
                <div className="absolute top-1/5 left-1/5 w-20 md:w-28 h-20 md:h-28 rounded-full bg-blue-400/50 blur-2xl animate-[pulse_2s_infinite]" />
                <div
                  className="absolute top-3/5 left-3/5 w-24 md:w-32 h-24 md:h-32 rounded-full bg-purple-400/50 blur-2xl animate-[pulse_2.5s_infinite]"
                  style={{ animationDelay: '0.8s' }}
                />
                <div
                  className="absolute top-2/5 left-4/5 w-16 md:w-24 h-16 md:h-24 rounded-full bg-pink-400/50 blur-2xl animate-[pulse_2s_infinite]"
                  style={{ animationDelay: '1.2s' }}
                />
              </div>

              {/* Subtle grid pattern */}
              <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHBhdGggZD0iTSAwIDEwIEwgMjAgMTAgTSAxMCAwIEwgMTAgMjAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iI2RkZGRkZCIgb3BhY2l0eT0iMC4zIiBzdHJva2Utd2lkdGg9IjEiLz48L3N2Zz4=')] opacity-30" />

              {/* Center loading icon from lucide-react */}
              <div className="absolute inset-0 flex items-center justify-center">
                <CookingPot className="w-14 h-14  text-gray-400/70 animate-[pulse_1.5s_infinite]" />
              </div>
            </div>
          </div>
        </div>
      </Card>
    </>
  )
}

export default ModelResultSkeleton
